/**
 * ApiClient — the shared transport for every /api/v1 call.
 *
 * One method: `request(endpoint, options)`, where `endpoint` is a locked
 * contract constant (e.g. ME_ENDPOINT from @web-v4/api-contract/v1/auth)
 * or any structural { method, path }. It resolves the SUCCESS ENVELOPE
 * (`{ data, request_id }`) and throws one of the three typed errors from
 * ./errors.js — callers branch on `instanceof` + `error.code`, never on
 * message text (contract guidelines §2).
 *
 * Out of scope ON PURPOSE (layered packages own these):
 *  - CSRF: header injection and the 419 refresh-retry dance live in
 *    @web-v4/csrf-client, which composes an ApiClient;
 *  - auth domain semantics (401-means-anonymous, captcha, ...):
 *    @web-v4/auth-client;
 *  - app/page URLs: @web-v4/router-utils.
 */
import { isErrorEnvelope, isSuccessEnvelope, } from '@web-v4/api-contract';
import { ApiError, ApiResponseError, ApiTimeoutError, ApiTransportError } from './errors.js';
import { DEFAULT_RETRY_POLICY, resolveRetryPolicy, retryDelayMs, sleep, } from './retry.js';
import { isValidRequestId, newRequestId, REQUEST_ID_HEADER } from './request-id.js';
/** Serializes query params deterministically (insertion order, URL-encoded). */
export function serializeQuery(query) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
        if (value === null || value === undefined) {
            continue;
        }
        if (Array.isArray(value)) {
            for (const item of value) {
                params.append(key, String(item));
            }
            continue;
        }
        params.append(key, String(value));
    }
    return params.toString();
}
const DEFAULT_TIMEOUT_MS = 15_000;
const BODY_SNIPPET_LEN = 200;
export class ApiClient {
    baseUrl;
    fetchImpl;
    defaultHeaders;
    timeoutMs;
    retryPolicy;
    credentials;
    idFactory;
    constructor(options = {}) {
        this.baseUrl = options.baseUrl ?? '';
        this.fetchImpl = options.fetch ?? globalThis.fetch?.bind(globalThis);
        if (typeof this.fetchImpl !== 'function') {
            throw new TypeError('ApiClient: no fetch available — pass options.fetch');
        }
        this.defaultHeaders = options.headers;
        this.timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
        this.retryPolicy = options.retry ?? DEFAULT_RETRY_POLICY;
        this.credentials = options.credentials ?? 'same-origin';
        this.idFactory = options.newRequestId ?? newRequestId;
    }
    /**
     * Performs one API call. Resolves the success envelope on a 2xx with a
     * `{data, request_id}` body; throws ApiError (error envelope),
     * ApiTransportError/ApiTimeoutError (no response), or ApiResponseError
     * (contract-violating response).
     */
    async request(endpoint, options = {}) {
        const method = endpoint.method;
        if (options.body !== undefined && (method === 'GET' || method === 'DELETE')) {
            throw new TypeError(`ApiClient: ${method} requests must not carry a body (${endpoint.path})`);
        }
        const requestId = this.resolveRequestId(options);
        const url = this.buildUrl(endpoint.path, options.query);
        const policy = resolveRetryPolicy(this.retryPolicy, options.retry, method);
        let lastError;
        for (let attempt = 0; attempt <= policy.retries; attempt++) {
            if (options.signal?.aborted) {
                throw new ApiTransportError({ message: 'request aborted before send', requestId });
            }
            if (attempt > 0) {
                await sleep(retryDelayMs(policy, attempt - 1));
            }
            try {
                return await this.attempt(method, url, requestId, options);
            }
            catch (error) {
                lastError = error;
                if (options.signal?.aborted || attempt >= policy.retries || !policy.isRetryable(error)) {
                    throw error;
                }
            }
        }
        // Unreachable (the loop either returns or throws), but satisfies the compiler.
        throw lastError;
    }
    /** One HTTP exchange: fetch + body read under a per-attempt timeout, then envelope parsing. */
    async attempt(method, url, requestId, options) {
        const timeoutMs = options.timeoutMs ?? this.timeoutMs;
        const controller = new AbortController();
        let timedOut = false;
        const timer = setTimeout(() => {
            timedOut = true;
            controller.abort();
        }, timeoutMs);
        const externalSignal = options.signal;
        const onExternalAbort = () => controller.abort();
        if (externalSignal) {
            if (externalSignal.aborted) {
                controller.abort();
            }
            else {
                externalSignal.addEventListener('abort', onExternalAbort, { once: true });
            }
        }
        let response;
        let text;
        try {
            response = await this.fetchImpl(url, {
                method,
                headers: this.buildHeaders(requestId, options),
                body: options.body === undefined ? null : JSON.stringify(options.body),
                credentials: this.credentials,
                cache: 'no-store',
                redirect: 'follow',
                signal: controller.signal,
            });
            text = await response.text();
        }
        catch (error) {
            if (timedOut) {
                throw new ApiTimeoutError({
                    message: `request timed out after ${timeoutMs}ms (${method} ${url})`,
                    requestId,
                    timeoutMs,
                    cause: error,
                });
            }
            if (controller.signal.aborted) {
                throw new ApiTransportError({ message: 'request aborted', requestId, cause: error });
            }
            throw new ApiTransportError({
                message: `network request failed (${method} ${url})`,
                requestId,
                cause: error,
            });
        }
        finally {
            clearTimeout(timer);
            externalSignal?.removeEventListener('abort', onExternalAbort);
        }
        return parseEnvelope(response, text, requestId);
    }
    buildUrl(path, query) {
        const qs = query ? serializeQuery(query) : '';
        return `${this.baseUrl}${path}${qs ? `?${qs}` : ''}`;
    }
    buildHeaders(requestId, options) {
        const headers = new Headers(this.defaultHeaders);
        new Headers(options.headers).forEach((value, key) => headers.set(key, value));
        if (!headers.has('Accept')) {
            headers.set('Accept', 'application/json');
        }
        if (options.body !== undefined && !headers.has('Content-Type')) {
            headers.set('Content-Type', 'application/json');
        }
        headers.set(REQUEST_ID_HEADER, requestId);
        return headers;
    }
    resolveRequestId(options) {
        const fromOption = options.requestId ?? new Headers(options.headers).get(REQUEST_ID_HEADER) ?? undefined;
        const id = fromOption ?? this.idFactory();
        if (!isValidRequestId(id)) {
            throw new TypeError(`ApiClient: invalid request id ${JSON.stringify(id)} — must be 1..64 chars of [A-Za-z0-9-_]`);
        }
        return id;
    }
}
/** Parses the HTTP response into a success envelope or throws the typed error. */
function parseEnvelope(response, text, sentRequestId) {
    const echoedId = response.headers.get(REQUEST_ID_HEADER) ?? sentRequestId;
    const status = response.status;
    if (text.length === 0) {
        throw new ApiResponseError({
            message: `empty response body (HTTP ${status}) — the contract requires an envelope`,
            status,
            requestId: echoedId,
        });
    }
    let body;
    try {
        body = JSON.parse(text);
    }
    catch {
        throw new ApiResponseError({
            message: `non-JSON response (HTTP ${status})`,
            status,
            requestId: echoedId,
            bodySnippet: text.slice(0, BODY_SNIPPET_LEN),
        });
    }
    if (typeof body !== 'object' || body === null) {
        throw new ApiResponseError({
            message: `malformed envelope (HTTP ${status}) — top level is not an object`,
            status,
            requestId: echoedId,
            bodySnippet: text.slice(0, BODY_SNIPPET_LEN),
        });
    }
    if (isErrorEnvelope(body)) {
        const errorBody = body.error;
        if (typeof errorBody.code !== 'string' || typeof errorBody.message !== 'string') {
            throw new ApiResponseError({
                message: `malformed error envelope (HTTP ${status}) — error.code/error.message missing`,
                status,
                requestId: echoedId,
                bodySnippet: text.slice(0, BODY_SNIPPET_LEN),
            });
        }
        throw new ApiError({
            code: errorBody.code,
            message: errorBody.message,
            fields: errorBody.fields,
            status,
            requestId: typeof body.request_id === 'string'
                ? body.request_id
                : echoedId,
        });
    }
    if (!response.ok) {
        throw new ApiResponseError({
            message: `HTTP ${status} without an error envelope`,
            status,
            requestId: echoedId,
            bodySnippet: text.slice(0, BODY_SNIPPET_LEN),
        });
    }
    if (isSuccessEnvelope(body)) {
        return body;
    }
    throw new ApiResponseError({
        message: `malformed envelope (HTTP ${status}) — neither "data" nor "error" present`,
        status,
        requestId: echoedId,
        bodySnippet: text.slice(0, BODY_SNIPPET_LEN),
    });
}
/** Convenience factory — equivalent to `new ApiClient(options)`. */
export function createApiClient(options = {}) {
    return new ApiClient(options);
}
//# sourceMappingURL=client.js.map